/* eslint-disable max-classes-per-file */
import { NgStyle } from "@angular/common";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { CardComponent } from "@components/cards/card/card.component";
import { CardsComponent } from "@components/cards/cards.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { CommandViewerComponent } from "@components/command-viewer/command-viewer.component";
import { BlockComponent } from "@components/container-block/block.component";
import { DatepickerIntervalComponent } from "@components/datepicker-interval/datepicker-interval.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapComponent } from "@components/map/map.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SliderComponent } from "@components/slider/slider.component";
import { SpanComponent } from "@components/span/span.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TableComponent } from "@components/table/table.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TagComponent } from "@components/tag/tag.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { of } from "rxjs";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ModelActionService } from "../../common/services/cqs/model-action.service";
import { CommandDto } from "../../common/services/cqs/models/command-dto";
import { FormBinderService } from "../../common/services/form-binder.service";
import { ValidationRuleDirective } from "../../common/validation/validation-rule.directive";
import { ExampleUsageTexts } from "./example-usage.texts";
import { ExampleUsageValidation } from "./example-usage.validation";

@Component({
    selector: "rask-example-usage",
    styleUrls: ["./example-usage.component.scss"],
    templateUrl: "./example-usage.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        BlockComponent,
        RouterLink,
        TabsComponent,
        TabComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        ValidationRuleDirective,
        InputNumberComponent,
        CheckboxComponent,
        SelectorComponent,
        DatepickerComponent,
        DatepickerIntervalComponent,
        TreeSelectorComponent,
        MultiSelectorComponent,
        TagComponent,
        RadiobuttonGroupComponent,
        TextmultilineComponent,
        TextareaComponent,
        SliderComponent,
        TableComponent,
        CardsComponent,
        CardComponent,
        SpanComponent,
        TogglePanelComponent,
        NgStyle,
        MapComponent,
        MapLayersComponent,
        HeaderComponent,
        SubmitButtonComponent,
        ButtonComponent,
        CommandViewerComponent
    ]
})
export class ExampleUsageComponent implements OnInit {

    validationContext = ExampleUsageValidation.init();
    form: UntypedFormGroup;
    model: ExampleModel;
    result: CommandDto[];
    showResults = false;
    selectorValues = [
        "ett",
        "två",
        "tre"
    ];
    groupedSelectorValues = [
        { category: "Träd",
            value: "Gran" },
        { category: "Träd",
            value: "Palm" },
        { category: "Djur",
            value: "Katt" },
        { category: "Djur",
            value: "Hund" },
        { category: "Djur",
            value: "Bäver" },
        { category: "Bilar",
            value: "Volvo" },
        { category: "Bilar",
            value: "BMW" }
    ];
    treeSelectorValues = [
        {
            text: "Item 1",
            value: "i1"
        },
        {
            children: [
                {
                    text: "Item 2c1",
                    value: "i2c1"
                },
                {
                    text: "Item 2c2",
                    value: "i2c2"
                },
                {
                    children: [
                        {
                            text: "Item 2c3c1",
                            value: "i2c3c1"
                        },
                        {
                            text: "Item 2c3c2 extra extra wiiiiiiiiiiiiiiiiiiide teeext",
                            value: "i2c3c2"
                        }
                    ],
                    text: "Item 2c3",
                    value: "i2c3"
                }
            ],
            text: "Item 2",
            value: "i2"
        },
        {
            text: "Item 3",
            value: "i3"
        }
    ] as SelectionItem[];

    treeSelectorSingleValues = [
        {
            text: "Item 1",
            value: "i1"
        },
        {
            text: "Item 2",
            value: "i2"
        },
        {
            text: "Item 3",
            value: "i3"
        }
    ] as SelectionItem[];

    multiSelectorValues = of([
        {
            text: "Item 1",
            groupName: "Group 1",
            value: "i1"
        },
        {
            text: "Item 2",
            groupName: "Group 2",
            value: "i2"
        },
        {
            text: "Item 3",
            groupName: "Group 1",
            value: "i3"
        },
        {
            text: "Item 4",
            groupName: "Group 1",
            value: "i4"
        },
        {
            text: "Item 5",
            groupName: "Group 2",
            value: "i5"
        }
    ]);

    tagValues = [
        "En tag",
        "Två taggar"
    ];

    tableTreeValuesFord = [
        {
            text: "Sport",
            value: "sport",
            children: [
                {
                    text: "Mustang",
                    value: "Mustang"
                },
                {
                    text: "GT",
                    value: "GT"
                }
            ]
        },
        {
            text: "Normal",
            value: "sport",
            children: [
                {
                    text: "Mondeo",
                    value: "Mondeo"
                },
                {
                    text: "Escort",
                    value: "Escort"
                }
            ]
        },
        {
            text: "SUV",
            value: "suv",
            children: [
                {
                    text: "Explorer",
                    value: "Explorer"
                },
                {
                    text: "EXPEDITON",
                    value: "EXPEDITON"
                }
            ]
        }

    ] as SelectionItem[];

    tableTreeValuesPorsche = [
        {
            text: "Sport",
            value: "sport",
            children: [
                {
                    text: "911 RS",
                    value: "911 RS"
                },
                {
                    text: "Boxter",
                    value: "Boxter"
                }
            ]
        },
        {
            text: "Normal",
            value: "Normal",
            children: [
                {
                    text: "Taycan",
                    value: "Taycan"
                },
                {
                    text: "Panamera",
                    value: "Panamera"
                }
            ]
        },
        {
            text: "SUV",
            value: "suv",
            children: [
                {
                    text: "Cayenne",
                    value: "Cayenne"
                }
            ]
        }

    ] as SelectionItem[];

    tableTreeValuesToyota = [
        {
            text: "Sport",
            value: "sport",
            children: [
                {
                    text: "Celica",
                    value: "Celica"
                },
                {
                    text: "Corolla",
                    value: "Corolla"
                }
            ]
        },
        {
            text: "Normal",
            value: "Normal",
            children: [
                {
                    text: "Yaris",
                    value: "Yaris"
                },
                {
                    text: "Prius",
                    value: "Prius"
                }
            ]
        },
        {
            text: "SUV",
            value: "suv",
            children: [
                {
                    text: "Landcruiser",
                    value: "Landcruiser"
                },
                {
                    text: "Highlander",
                    value: "Highlander"
                }
            ]
        }

    ] as SelectionItem[];

    tableData = [
        {
            make: "Toyota",
            model: ["Celica"],
            price: 35000,
            checked: true,
            text: ""
        },
        {
            make: "Ford",
            model: ["Mondeo"],
            price: 32000,
            checked: true
        },
        {
            make: "Porsche",
            model: ["Boxter"],
            price: 72000,
            checked: false
        }
    ];

    columns = [
        {
            field: "checked",
            headerName: "",
            type: "checkbox",
            editable: true,
            maxWidth: 50,
            footerText: "Summa"
        },
        {
            field: "make",
            placeholder: "Välj tillverkare",
            type: "selector",
            editable: true,
            sortable: true,
            values: [
                "Toyota",
                "Ford",
                "Porsche"
            ],
            filter: true
        },
        {
            field: "price",
            rule: this.validationContext.current.moreThanZero,
            type: "number",
            editable: true,
            sortable: true,
            footerAgg: "sum",
            filter: true,
            headerParams: {
                iconClass: "fal fa-info-circle",
                popoverMessage: "En informationstext"
            }
        },
        {
            field: "model",
            placeholder: "Välj modell",
            type: "treeselector",
            editable: true,
            values: this.getTableValues.bind(this)
        },
        {
            field: "text",
            rule: this.validationContext.current.textOnlyNumbers,
            type: "text",
            editable: true
        },
        {
            field: "action",
            type: "button",
            text: "Knapp",
            // eslint-disable-next-line no-alert
            onClick: (v) => alert("blip"),
            footerAgg: (data: any[]) => `Totalt: ${data.length} st`
        }
    ] as any[] as RaskColumn[];

    texts = ExampleUsageTexts.texts;

    private readonly formBinder = inject(FormBinderService);
    private readonly modelAction = inject(ModelActionService);

    getTableValues(row) {
        // eslint-disable-next-line eqeqeq
        if (row == undefined) {
            return [];
        }
        switch (row.make) {
            case "Toyota":
                return this.tableTreeValuesToyota;
            case "Ford":
                return this.tableTreeValuesFord;
            case "Porsche":
                return this.tableTreeValuesPorsche;
        }
        return [];
    }

    ngOnInit() {
        this.reset();
    }

    reset() {
        this.modelAction.reset();
        this.model = new ExampleModel();
        this.modelAction.bindModel(this.model, "ExampleModel");
        this.modelAction.bindModel(this.tableData, "TableRow");
        this.form = this.formBinder.CreateGroup(this.model);
        this.form.enable();
        this.listenOnFormChanges(this.form);
        this.updateLayout();
        this.closeResults();
    }

    listenOnFormChanges(form: UntypedFormGroup) {
        this.form.valueChanges.subscribe((model) => {
            this.updateLayout();
        });
    }

    updateLayout() {
        this.form.get("disabledText").disable({ emitEvent: false });
    }

    selectorGroupBy = (v) => v.category;
    selectorFormatter = (v) => v.value;
    selectorOutputFormatter = (v) => v ? v.value : null;
    sliderOpacityValueFormatter = (item) => `${Math.round(item * 100)} %`;

    save() {
        // Eg ska vi köra en this.modelAction.pushCommandQueue() här
        this.result = Object.assign([], this.modelAction.commandQueue);
        this.modelAction.reset();
        this.showResults = true;
    }

    closeResults() {
        this.showResults = false;
    }

    canSave() {
        return this.modelAction.commandQueue.length > 0;
    }

    pop(e) {
        // eslint-disable-next-line no-console
        console.log(e);
    }

    popPage = () => {
        // eslint-disable-next-line no-console
        console.log("Page action!");
    };

    sliderOpacityChanged(e) {
        if (isNaN(e)) {
            return;
        }

        this.model.opacity = e;
    }

    customSearchFn(term: string, item: any) {
        term = term.toLowerCase();
        return item.text.toLowerCase().indexOf(term) > -1 || item.value.toLowerCase().indexOf(term) > -1;
    }
}

// Detta görs automatiskt när vi genererar upp modeller från C#
export class ExampleModel {
    id: number;
    name: string;
    longText: string;
    extraLongText: string;
    disabledText: string;
    surname: string;
    age: number;
    accept: boolean;
    otherText: string;
    selection: string;
    selectionGrouped: string;
    datePicker: string;
    monthPicker: string;
    treeSelector: any[];
    treeSelectorSingle: any[];
    multiSelector: any[];
    tag: any[];
    test: string;
    radioButtons: string;
    textArea: string;
    opacity: number;
    constructor() {
        this.id = 1;
        this.name = undefined;
        this.surname = undefined;
        this.age = undefined;
        this.accept = true;
        this.longText = undefined;
        this.extraLongText = undefined;
        this.disabledText = "Disabled default text";
        this.otherText = undefined;
        this.selection = null;
        this.selectionGrouped = null;
        this.datePicker = undefined;
        this.monthPicker = undefined;
        this.treeSelector = [
            "i1",
            "i2c1",
            "i2c3c1"
        ];
        this.treeSelectorSingle = [];
        this.multiSelector = ["i2"];
        this.tag = [];
        this.test = undefined;
        this.radioButtons = "111";
        this.textArea = "abc123";
        this.opacity = 0.22;
    }
}
